/**
 * Hero.js
 * The heroes basic class
 */

import { Game } from "pixi_core";
import { HeroState, Heroes, CheatEvents, GameEvents } from "../Constants";

export class Hero {
    /**
     * @public
     * @param {Object} heroData
     */
    constructor(heroData) {
        /** @protected @type {Number} */
        this._hp = heroData.HP;

        /** @protected @type {Number} */
        this._abilityCooldown = heroData.AbilityCooldown;

        /** @protected @type {HeroState} */
        this._heroState = HeroState.eUNDEFINED;

        /** @protected @type {Heroes} */
        this._name = heroData.name;

        /** @protected @type {Boolean} */
        this._cheatInfiniteAbility = Game.windows.cheatsMenu.getValue("AvengersInfiniteAbility");

        /** @protected @type {Number} */
        this._charge = this._cheatInfiniteAbility ? this._abilityCooldown : 0;

        /** @protected @type {Number} */
        this._stunDuration = 0;

        /** @protected @type {Boolean} */
        this._stunStarted = false;

        Game.on(CheatEvents.eAVENGERS_INFINITE_ABILITY, this._onCheatsAvengersInfiniteAbility, this);
    }

    /**
     * @private
     * @param {HeroState} state
     */
    _setState(state) {
        if (this._heroState === state) {
            return;
        }

        this._heroState = state;

        switch (this._heroState) {
            case HeroState.eIDLE:
                this._initStateIdle();
                break;
            case HeroState.eATTACKING:
                this._initStateAttacking();
                break;
            case HeroState.eDYING:
                this._initStateDying();
                break;
            case HeroState.eDONE:
                this._initStateDone();
                break;
        }
    }

    /**
     * @public
     * @param {Number} deltaTime
     */
    updateLoop(deltaTime) {
        this._updateState(deltaTime);
    }

    /**
     * @private
     * @param {Number} deltaTime
     */
    _updateState(deltaTime) {
        switch (this._heroState) {
            case HeroState.eIDLE:
                this._updateStateIdle(deltaTime);
                break;
            case HeroState.eATTACKING:
                this._updateStateAttacking(deltaTime);
                break;
            case HeroState.eDYING:
                this._updateStateDying(deltaTime);
                break;
            case HeroState.eDONE:
                this._updateStateDone(deltaTime);
                break;
        }
    }

    /**
     * @private
     */
    _initStateIdle() {}

    /**
     * @private
     */
    _initStateAttacking() {}

    /**
     * @private
     */
    _initStateDying() {}

    /**
     * @private
     */
    _initStateDone() {}

    /**
     * @private
     * @param {Number} deltaTime
     */
    _updateStateIdle(deltaTime) {}

    /**
     * @private
     * @param {Number} deltaTime
     */
    _updateStateAttacking(deltaTime) {
        this._setState(HeroState.eIDLE);
    }

    /**
     * @private
     * @param {Number} deltaTime
     */
    _updateStateDying(deltaTime) {
        this._setState(HeroState.eDONE);
    }

    /**
     * @private
     * @param {Number} deltaTime
     */
    _updateStateDone(deltaTime) {}

    /**
     * The player made a match ==> lower the ability cooldown
     * @public
     */
    chargeAbility() {
        if (this._charge === this._abilityCooldown - 1) {
            Game.audio.playSfx("IDSND_PLAYER_ABIL_READY");
        }
        ++this._charge;

        if (this._charge > this._abilityCooldown) {
            this._charge = this._abilityCooldown;
        }

        Game.fire(GameEvents.eHERO_ABILITY_CHARGING, this._name, this._charge, this._abilityCooldown);

        if (this._charge === this._abilityCooldown) {
            Game.fire(GameEvents.eHERO_ABILITY_CHARGED, this._name);
        }
    }

    /**
     * Adds stun counters to the hero.
     * @public
     * @param {Number} heroId
     * @param {Number} duration
     */
    stunHero(heroId, duration) {
        // Add a lock to take into account that stun counters are cleared at the beginning of the heroes' turn.
        if (this._stunDuration === 0) {
            this._stunStarted = true;
        }
        this._stunDuration += duration;

        Game.fire(GameEvents.eHEROES_STUNNED_VISUAL, heroId, this._stunDuration);
    }

    /**
     * Removes one stun counter from the hero.
     * @public
     */
    removeStun() {
        if (this._stunStarted) {
            this._stunStarted = false;
            return;
        }
        if (this._stunDuration > 0) {
            --this._stunDuration;
        }
    }

    /**
     * Sets the hero's charge if the infinite ability cheat is enabled.
     * @public
     */
    setCheatCharge() {
        if (this._cheatInfiniteAbility) {
            this._charge = this._abilityCooldown;
            this.chargeAbility();
        }
    }

    /**
     * @public
     * @returns {Boolean}
     */
    canUseAbility() {
        return this._charge >= this._abilityCooldown && this._stunDuration === 0;
    }

    /**
     * @public
     */
    useAbility() {
        if (!this._cheatInfiniteAbility) {
            this._charge = 0;
        }
    }

    /**
     * Returns the hero's hp
     * @public
     * @returns {Number}
     */
    get hp() {
        return this._hp;
    }

    /**
     * @public
     * @returns {Heroes}
     */
    get name() {
        return this._name;
    }

    /**
     * Returns the hero's cooldown
     * @public
     * @returns {Number}
     */
    get abilityCooldown() {
        return this._abilityCooldown;
    }

    /**
     * @public
     * @returns {Number}
     */
    get charge() {
        return this._charge;
    }

    /**
     * @public
     * @returns {Number}
     */
    get stunDuration() {
        return this._stunDuration;
    }

    /**
     * Returns the hero's state
     * @public
     * @returns {HeroState}
     */
    get state() {
        return this._heroState;
    }

    /**
     * @public
     */
    destroy() {
        Game.off(CheatEvents.eAVENGERS_INFINITE_ABILITY, this._onCheatsAvengersInfiniteAbility, this);
    }

    /**
     * @private
     * @param {Boolean} enabled
     */
    _onCheatsAvengersInfiniteAbility(enabled) {
        this._cheatInfiniteAbility = enabled;

        if (this._cheatInfiniteAbility) {
            this._charge = this._abilityCooldown;
            this.chargeAbility();
        }
    }
}
